import { FC } from "react";
import styles from "./price-details.module.scss";
import { Vehicle } from "../../../types/vehicle";
import { ActionButtonData, ActionButtonPosition, ScriptData } from "../../../types/site";
import { ActionButtonPrefetchedData } from "../../common-components/vdp-buttons/actionButton";
import {
  IVDPBlockData,
  IVDPPageBlock,
  VDPBlockGlobalInitData,
  VDPBlockPageData,
  VDPBlockPosition,
  VDPComponentGlobalInitHook,
  VDPComponentServerHook,
} from "../../../types/vdp";
import {
  prefetchActionButtonData,
  processActionButtonCssVariables,
  processActionButtonVariables,
} from "../../../utils/actionButtons";
import PriceDetailsClient from "./price-details-client";
import { getTranslationModule } from "../../../utils/translations";
import { PriceDisclaimerPosition } from "@/types/blocks";

const PriceDetails: FC<IVDPPageBlock> = ({
  siteData,
  pageContext,
  renderingPosition,
  vdpGlobalData,
  translations,
  config,
}) => {
  const t = getTranslationModule(translations, "vdp");
  const vehicle = vdpGlobalData.vehicle;
  const vehicleCardData = vdpGlobalData.vehicleCardData;

  const priceDisclaimer = vehicleCardData?.priceDisclaimer;
  const priceDisclaimerPosition = vehicleCardData?.priceDisclaimerPosition || PriceDisclaimerPosition.TOOLTIP;

  if (renderingPosition === VDPBlockPosition.LEFT && !vdpGlobalData.isSingleColumnRendering) {
    const carSubtitle = `${vehicle.year} ${vehicle.make}`;
    const carTitle = `${vehicle.model} ${vehicle.trim} ${vehicle.drivetrain} ${vehicle.transmission}`;

    return (
      <div className={styles.carInfo}>
        <span title={carSubtitle} className={styles.carInfoSubtitle}>
          {carSubtitle}
        </span>
        <h1 title={carTitle} className={styles.carInfoTitle}>
          {carTitle}
        </h1>
      </div>
    );
  }

  const priceProperties = vehicleCardData?.priceTab.properties || [];

  const actionButtonsWithPrefetchedData: ActionButtonPrefetchedData[] = vdpGlobalData.actionButtons.map(
    (item, index) => ({
      actionButtonData: item,
      prefetchedValue: vehicle.actionButtonsData![index],
    }),
  );

  const bottomActionButtons = actionButtonsWithPrefetchedData.filter(
    (item) => item.actionButtonData.position === ActionButtonPosition.BOTTOM,
  );

  return (
    <section className={styles.container}>
      <h3 className={styles.title}>{t("price_details")}</h3>
      <PriceDetailsClient
        siteData={siteData}
        vehicle={vehicle}
        pageContext={pageContext}
        buttons={bottomActionButtons}
        priceProperties={priceProperties}
        translations={translations}
        showClickToCall={config.showClickToCall}
        contentBelowPrices={vehicleCardData?.contentBelowPrices}
        priceDisclaimer={priceDisclaimerPosition === PriceDisclaimerPosition.BOTTOM ? priceDisclaimer : ""}
      />
    </section>
  );
};

export const VdpPriceDetailsServerSideHook: VDPComponentServerHook = async (
  config: any,
  vehicle: Vehicle,
  pageData: VDPBlockPageData,
) => {
  const actionButtons: ActionButtonData[] = config.actionButtons ?? [];

  actionButtons.forEach((item: ActionButtonData) => {
    if (item.cssData) {
      const css = processActionButtonCssVariables(item.cssData, item.actionButtonData);
      pageData.styles.push({ id: item.buttonName, content: css });
      delete item.cssData;
    }
    if (item.externalScript) {
      const buttonScript: ScriptData = { ...item.externalScript };
      if (buttonScript.content) {
        buttonScript.content = processActionButtonVariables(buttonScript.content, item.actionButtonData);
      }
      if (buttonScript.url) {
        buttonScript.url = processActionButtonVariables(buttonScript.url, item.actionButtonData);
      }

      if (buttonScript.customAttributes) {
        buttonScript.customAttributes.forEach((attribute) => {
          if (attribute.value) {
            attribute.value = processActionButtonVariables(attribute.value, item.actionButtonData);
          }
        });
      }

      pageData.scripts.push(buttonScript);
    }
  });

  await prefetchActionButtonData([vehicle], actionButtons);
  config.actionButtons = actionButtons;
  return config;
};

export const VDPPriceDetailsGlobalInitHook: VDPComponentGlobalInitHook = (
  config: any,
  vehicle: Vehicle,
  vdpGlobalData: VDPBlockGlobalInitData,
) => {
  const actionButtons: ActionButtonData[] = config.actionButtons;
  actionButtons.forEach((item) => {
    vdpGlobalData.actionButtons.push(item);
  });
};

const VDPBlockData: IVDPBlockData = {
  component: (props: IVDPPageBlock, key: any) => (
    <PriceDetails
      key={key}
      config={props.config}
      siteData={props.siteData}
      pageContext={props.pageContext}
      renderingPosition={props.renderingPosition}
      vdpGlobalData={props.vdpGlobalData}
      translations={props.translations}
    />
  ),
  position: VDPBlockPosition.RIGHT,
};

export default VDPBlockData;
